/**
 * NoteTooltip Component
 *
 * Small note icon that reveals its text on hover, focus, or tap (click
 * toggles — iOS Safari doesn't focus buttons on tap). The panel uses
 * position:fixed computed from the trigger's viewport rect so it can't be
 * clipped by an overflow-y-auto ancestor (e.g. a scrolling modal list).
 */

import { useRef, useState } from 'react';

interface NoteTooltipProps {
  note: string;
  /** Accessible label for the icon button (default: "View note") */
  label?: string;
}

const PANEL_WIDTH = 288; // matches w-72

export default function NoteTooltip({ note, label = 'View note' }: NoteTooltipProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const show = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      // Anchor below the icon, clamped so the panel stays inside the viewport.
      const left = Math.max(8, Math.min(rect.left, window.innerWidth - PANEL_WIDTH - 8));
      setPosition({ top: rect.bottom + 6, left });
    }
    setOpen(true);
  };

  const hide = () => setOpen(false);

  return (
    <span className="inline-flex">
      <button
        ref={triggerRef}
        type="button"
        aria-label={label}
        aria-expanded={open}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        onClick={() => (open ? hide() : show())}
        onKeyDown={(e) => {
          if (e.key === 'Escape') hide();
        }}
        className="w-5 h-5 inline-flex items-center justify-center text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-rack"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 8h10M7 12h6m-8.5 8.5L7 18h11a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v13.6a.9.9 0 00.5.9z"
          />
        </svg>
      </button>
      {open && (
        <span
          role="tooltip"
          style={{ position: 'fixed', top: position.top, left: position.left, width: PANEL_WIDTH }}
          className="z-[60] bg-gray-900 text-white text-xs rounded-lg p-3 shadow-lg whitespace-pre-wrap break-words"
        >
          {note}
        </span>
      )}
    </span>
  );
}
